(() => {
  'use strict';
  const GROUPS = 'deliveryGroups(first:10){nodes{deliveryOptions{title description estimatedCost{amount currencyCode}}}pageInfo{hasNextPage endCursor}}';
  const CREATE = `mutation GaloreFreightEstimate($input:CartInput!){cartCreate(input:$input){cart{id totalQuantity ${GROUPS}}userErrors{field message code}warnings{code message}}}`;
  const READ = `query GaloreFreightRates($id:ID!){cart(id:$id){${GROUPS}}}`;
  const REGIONS = {AUK:'Auckland',BOP:'Bay of Plenty',CAN:'Canterbury',CIT:'Chatham Islands',GIS:'Gisborne',HKB:"Hawke's Bay",MWT:'Manawatu-Wanganui',MBH:'Marlborough',NSN:'Nelson',NTL:'Northland',OTA:'Otago',STL:'Southland',TKI:'Taranaki',TAS:'Tasman',WKO:'Waikato',WGN:'Wellington',WTC:'West Coast'};
  const sleep = (ms, signal) => new Promise((resolve,reject) => {
    if (signal.aborted) { reject(new DOMException('Aborted','AbortError')); return; }
    const stop = () => { clearTimeout(timer); reject(new DOMException('Aborted','AbortError')); };
    const timer = setTimeout(() => {signal.removeEventListener('abort',stop);resolve();},ms);
    signal.addEventListener('abort',stop,{once:true});
  });
  const fingerprint = cart => JSON.stringify([cart.currency,cart.total_price,cart.items.map(i=>[i.key,i.quantity,i.final_line_price])]);
  const classify = name => /depot|collection/i.test(name) ? 'depot' : /non[\s-]*rural|urban/i.test(name) ? 'urban' : /rural/i.test(name) ? 'rural' : 'other';
  class CGFreight extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.strings = JSON.parse(this.querySelector('[data-freight-strings]').textContent);
      this.region = this.querySelector('[data-region]');
      this.zip = this.querySelector('[data-postcode]');
      this.method = this.querySelector('[data-method]');
      this.button = this.querySelector('[data-calculate]');
      this.result = this.querySelector('[data-freight-result]');
      this.form = this.closest('.cgp-buy')?.querySelector('form.cgp-form') || null;
      this.version = 0;
      this.bound = new AbortController();
      const options = {signal:this.bound.signal};
      this.button.hidden = false;
      this.button.addEventListener('click',()=>this.calculate(),options);
      const invalidate = () => {
        this.version++;
        this.controller?.abort();
        this.button.disabled = false;
        this.result.removeAttribute('aria-busy');
        if (this.result.textContent) this.result.textContent = this.strings.changed;
        const note = this.querySelector('[data-method-note]');
        if(note) {note.hidden=this.method?.value!=='depot';}
      };
      this.addEventListener('input',invalidate,options);
      this.addEventListener('change',invalidate,options);
      if(this.form) {this.form.addEventListener('input',invalidate,options);this.form.addEventListener('change',invalidate,options);}
      const cartForm = document.querySelector('.cart-page form');
      if(this.dataset.mode==='cart' && cartForm) {
        cartForm.addEventListener('input',()=>{invalidate();this.result.textContent=this.strings.update_cart;},options);
      }
      this.addEventListener('keydown',event=>{
        if(event.key==='Enter' && event.target.matches('input,select')) {event.preventDefault();event.stopPropagation();this.calculate();}
      },options);
    }
    disconnectedCallback() {this.controller?.abort();this.bound?.abort();this.ready=false;}
    selection() {
      if(!this.form) throw new Error('selection');
      const data=new FormData(this.form), id=String(data.get('id')||''), quantity=Number(data.get('quantity')||1);
      if(!/^\d+$/.test(id)||!Number.isInteger(quantity)||quantity<1||quantity>250) throw new Error('selection');
      return {merchandiseId:`gid://shopify/ProductVariant/${id}`,quantity};
    }
    async json(url,options) {
      const response=await fetch(url,options);
      if(!response.ok) throw new Error('request');
      return response.json();
    }
    async graph(query,variables,signal) {
      const data=await this.json(this.dataset.endpoint,{method:'POST',credentials:'omit',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({query,variables}),signal});
      if(data.errors?.length||!data.data) throw new Error('request');
      return data.data;
    }
    async productRates(signal) {
      const line=this.selection(), signature=JSON.stringify(line);
      const input={lines:[line],buyerIdentity:{countryCode:'NZ'},delivery:{addresses:[{selected:true,oneTimeUse:true,address:{deliveryAddress:{countryCode:'NZ',provinceCode:this.region.value,zip:this.zip.value.trim()}}}]}};
      const data=await this.graph(CREATE,{input},signal);
      const payload=data.cartCreate;
      if(!payload||payload.userErrors?.length||payload.warnings?.length||!payload.cart||payload.cart.totalQuantity!==line.quantity) throw new Error('selection');
      const id=payload.cart.id;
      let cart=payload.cart;
      for(let i=0;i<4 && (!cart.deliveryGroups.nodes.length||cart.deliveryGroups.nodes.some(g=>!g.deliveryOptions.length));i++) {
        await sleep(650,signal);cart=(await this.graph(READ,{id},signal)).cart;
        if(!cart) throw new Error('request');
      }
      if(JSON.stringify(this.selection())!==signature) throw new Error('changed');
      if(cart.deliveryGroups.pageInfo.hasNextPage) throw new Error('partial');
      const groups=cart.deliveryGroups.nodes.map(g=>g.deliveryOptions.map(r=>({name:r.title,amount:r.estimatedCost.amount,currency:r.estimatedCost.currencyCode})));
      if(!groups.length||groups.some(g=>!g.length)) throw new Error('none');
      return groups;
    }
    async cartRates(signal) {
      const root=this.dataset.root||'/';
      const getCart=()=>this.json(`${root}cart.js`,{headers:{Accept:'application/json'},credentials:'same-origin',signal});
      const cart=await getCart();
      if(!cart.item_count) throw new Error('empty');
      const inputs=[...document.querySelectorAll('.cart-page [name="updates[]"]')];
      if(inputs.length===cart.items.length && inputs.some((el,i)=>Number(el.value)!==cart.items[i].quantity)) throw new Error('update_cart');
      const signature=fingerprint(cart);
      const query=new URLSearchParams({'shipping_address[country]':'New Zealand','shipping_address[province]':REGIONS[this.region.value],'shipping_address[zip]':this.zip.value.trim()});
      const response=await fetch(`${root}cart/prepare_shipping_rates.json?${query}`,{method:'POST',credentials:'same-origin',headers:{Accept:'application/json'},signal});
      if(!response.ok) throw new Error('request');
      for(let i=0;i<14;i++) {
        if(i) await sleep(700,signal);
        const rates=await this.json(`${root}cart/async_shipping_rates.json?${query}`,{credentials:'same-origin',headers:{Accept:'application/json'},signal});
        if(rates?.shipping_rates) {
          if(fingerprint(await getCart())!==signature) throw new Error('changed');
          if(!rates.shipping_rates.length) throw new Error('none');
          return [rates.shipping_rates.map(r=>({name:r.name,amount:r.price,currency:r.currency||cart.currency||'NZD'}))];
        }
      }
      throw new Error('request');
    }
    render(groups) {
      const method=this.method?.value||'all';
      const filtered=groups.map(group=>method==='all'?group:group.filter(rate=>classify(rate.name)===method));
      if(filtered.some(group=>!group.length)) throw new Error('none');
      const fragment=document.createDocumentFragment();
      filtered.forEach((group,index)=>{
        if(filtered.length>1) {const p=document.createElement('p');p.textContent=`${this.strings.shipment} ${index+1}. ${this.strings.separate}`;fragment.append(p);}
        const ul=document.createElement('ul');
        group.forEach(rate=>{
          const li=document.createElement('li'),name=document.createElement('span'),price=document.createElement('strong');
          const amount=Number(rate.amount);
          if(!Number.isFinite(amount)||amount<0||!/^\d+(\.\d+)?$/.test(String(rate.amount))) throw new Error('request');
          name.textContent=rate.name;
          price.textContent=/quote/i.test(rate.name)?this.strings.quote:new Intl.NumberFormat('en-NZ',{style:'currency',currency:rate.currency||'NZD'}).format(amount);
          li.append(name,price);ul.append(li);
        });fragment.append(ul);
      });
      const note=document.createElement('p');note.textContent=this.strings.confirm;fragment.append(note);
      this.result.replaceChildren(fragment);
    }
    async calculate() {
      if(this.button.disabled) return;
      if(!REGIONS[this.region.value]||!/^\d{4}$/.test(this.zip.value.trim())) {
        this.result.textContent=this.strings.invalid;(!this.region.value?this.region:this.zip).focus();return;
      }
      const version=++this.version;
      this.controller?.abort();const controller=new AbortController();this.controller=controller;
      const timeout=setTimeout(()=>controller.abort(),18000);
      this.button.disabled=true;this.result.setAttribute('aria-busy','true');this.result.textContent=this.strings.loading;
      try {
        const groups=await (this.dataset.mode==='cart'?this.cartRates(controller.signal):this.productRates(controller.signal));
        if(this.isConnected&&version===this.version) this.render(groups);
      } catch(error) {
        if(this.isConnected&&version===this.version) this.result.textContent=this.strings[error.message]||this.strings.error;
      } finally {
        clearTimeout(timeout);
        if(version===this.version){this.button.disabled=false;this.result.removeAttribute('aria-busy');}
      }
    }
  }
  if(!customElements.get('cg-freight')) customElements.define('cg-freight',CGFreight);
  function mount() {
    const template=document.getElementById('CGFreightTemplate');
    if(template) {
      const target=document.querySelector('.cgp-buy')||document.querySelector('.cart-page');
      if(target&&!target.querySelector('cg-freight')) {
        const fragment=template.content.cloneNode(true);
        const old=target.querySelector('.cgp-delivery-summary');
        if(old)old.replaceWith(fragment);else target.append(fragment);
      }
    }
    const toc=document.querySelector('[data-cg-policy-toc]');
    if(toc&&!toc.childElementCount)document.querySelectorAll('.shopify-policy__body h2,.shopify-policy__body h3').forEach((heading,i)=>{
      if(!heading.id)heading.id=`policy-section-${i+1}`;
      const link=document.createElement('a');link.href=`#${heading.id}`;link.textContent=heading.textContent;toc.append(link);
    });
    document.querySelectorAll('.cg-care-links a').forEach(link=>{if(new URL(link.href,location.href).pathname===location.pathname)link.setAttribute('aria-current','page');});
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',mount,{once:true});else mount();
  document.addEventListener('shopify:section:load',mount);
})();
